import type { User } from '../models/User';
import { TEAM_DATA, type TeamKey } from '../models/TeamConstants';
import { useQuery } from '@tanstack/react-query';
import { getLeaderboard } from '../services/usersService';

export const TEAMS = Object.keys(TEAM_DATA) as TeamKey[];

export type LeaderboardPeriod = 'week' | 'month' | 'alltime';

interface LeaderboardData {
  allTime: User[];
  byTeam: Record<TeamKey, User[]>;
}

const LEADERBOARD_SIZE = 10;

function getScoreForPeriod(user: User, period: LeaderboardPeriod): number {
  if (period === 'week') return user.weeklyCorrect;
  if (period === 'month') return user.monthlyCorrect;
  return user.totalCorrectAnswers;
}

function sortAndSlice(users: User[], period: LeaderboardPeriod): User[] {
  return [...users]
    .sort((a, b) => getScoreForPeriod(b, period) - getScoreForPeriod(a, period))
    .slice(0, LEADERBOARD_SIZE);
}

export function useLeaderboardData(users: User[], period: LeaderboardPeriod): LeaderboardData {
  return {
    allTime: sortAndSlice(users, period),
    byTeam: Object.fromEntries(
      TEAMS.map(team => [team, sortAndSlice(users.filter(u => u.team === team), period)])
    ) as Record<TeamKey, User[]>,
  };
}

// Returns the correct answer count for a user given the active period
export function getCorrectForPeriod(user: User, period: LeaderboardPeriod): number {
  return getScoreForPeriod(user, period);
}

// Get leaderboard users for a given period
export const useLeaderboard = (period: string, count: number = 10) => {
  return useQuery({
    queryKey: ['leaderboard', period],
    queryFn: () => getLeaderboard(period, count),
    staleTime: 1000 * 60 * 2,
    retry: 1,
  })
}